import { useMemo, useCallback, useState, useContext, useEffect } from 'react';
import useSWR, { mutate } from 'swr';
import { sdk } from 'sdk';
import { Account, Address } from '@loomstateorg/shared-frontend/types/account';
import { revalidateOptions } from 'frontastic';
import { GlobalContext } from 'frontastic/context/globalContext';
import { GetAccountResult, RegisterAccount, UpdateAccount, UseAccountReturn } from './types';
import { isAccountDeactivatedError } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/authentication/login/errors';

const useAccount = (): UseAccountReturn => {
  const extensions = sdk.composableCommerce;
  const { syncCountryFilterFromAccount } = useContext(GlobalContext);
  const [refreshTimestamp, setRefreshTimestamp] = useState<number>(0);

  const result = useSWR(
    '/action/account/getAccount',
    () => {
      return extensions.account.getAccount();
    },
    revalidateOptions,
  );

  const data = useMemo(() => {
    if (result.data?.isError) return { loggedIn: false, accountLoading: false, error: result.error };

    const account = (result.data?.data as GetAccountResult)?.account as Account;

    if (account?.accountId) {
      return { account, loggedIn: true, accountLoading: false };
    }

    return {
      loggedIn: false,
      account: undefined,
      accountLoading: true,
      error: result.error,
    };
  }, [result]);

  useEffect(() => {
    syncCountryFilterFromAccount(data.account);
  }, [data.account, syncCountryFilterFromAccount]);

  let shippingAddresses = useMemo(() => {
    if (!data.account) return [];
    return (data.account.addresses ?? [])?.filter((address) => address?.isShippingAddress);
  }, [data.account]);

  const getShippingAddress = useCallback(
    async (searchValue: string, addressType: string, addressSearchType?: 'shipping' | 'billing') => {
      const payload: Record<string, unknown> = { searchValue, addressType };

      if (addressSearchType === 'shipping') {
        payload.defaultShippingAddress = true;
      }

      if (addressSearchType === 'billing') {
        payload.defaultBillingAddress = true;
      }

      const res = await sdk.callAction<Address[]>({
        actionName: 'account/getAddress',
        payload,
      });
      shippingAddresses = res.isError ? ([] as Address[]) : res?.data;
      return shippingAddresses;
    },
    [shippingAddresses],
  );

  const billingAddresses = useMemo(() => {
    if (!data.account) return [];

    return (data.account.addresses ?? [])?.filter((address) => address?.isBillingAddress);
  }, [data.account]);

  const defaultShippingAddress = useMemo(() => {
    return data.account?.addresses?.find((address) => address?.isDefaultShippingAddress);
  }, [data.account]);

  const addresses = useMemo(() => {
    return data.account?.addresses;
  }, [data.account]);

  const defaultBillingAddress = useMemo(() => {
    return data.account?.addresses?.find((address) => address?.isDefaultBillingAddress);
  }, [data.account]);

  const login = async (
    email: string,
    password: string,
    remember?: boolean,
  ): Promise<Account & { loginError?: string }> => {
    const extensions = sdk.composableCommerce;

    const payload = {
      email,
      password,
      remember,
    };

    const res = await extensions.account.login(payload);

    mutate('/action/account/getAccount');
    mutate('/action/cart/getCart');
    mutate('/action/wishlist/getWishlist');

    if (res.isError && isAccountDeactivatedError(res.error)) throw res.error;
    return res.isError ? { ...({} as Account), loginError: res.error.message } : res.data;
  };

  const logout = useCallback(async () => {
    const extensions = sdk.composableCommerce;

    await extensions.account.logout();

    mutate('/action/account/getAccount');
    mutate('/action/cart/getCart');
    mutate('/action/wishlist/getWishlist');
  }, []);

  const register = useCallback(async (account: RegisterAccount): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.register(account);

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const getCustomerGroup = useCallback(async (): Promise<Account> => {
    const res = await sdk.callAction<Account>({
      actionName: 'account/getCustomerGroup',
      payload: { accountId: data.account?.accountId },
    });

    // Removed mutate call that was causing infinite loop
    // mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, [data.account?.accountId]);

  const confirm = useCallback(async (token: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.confirm({ token });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const requestConfirmationEmail = useCallback(async (email: string, password: string): Promise<void> => {
    const extensions = sdk.composableCommerce;

    const payload = {
      email,
      password,
    };

    await extensions.account.requestConfirmationEmail(payload);
  }, []);

  const changePassword = useCallback(async (oldPassword: string, newPassword: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.changePassword({ oldPassword, newPassword });

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const requestPasswordReset = useCallback(async (email: string): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const payload = {
      email,
    };

    const res = await extensions.account.requestResetPassword(payload);

    return res;
  }, []);

  const resetPassword = useCallback(async (token: string, newPassword: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.resetPassword({ token, newPassword });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const update = useCallback(async (account: UpdateAccount): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.updateAccount(account);

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const addIsSubscribedType = useCallback(async () => {
    const extensions = sdk.composableCommerce;

    const response = await extensions.account.getAccount();

    if (response.isError || !('account' in response.data)) return {} as Account;

    const res = await sdk.callAction<Account>({
      actionName: 'account/addIsSubscribedType',
      payload: { account: response.data.account },
    });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const updateSubscription = useCallback(async (isSubscribed: boolean) => {
    const extensions = sdk.composableCommerce;

    const response = await extensions.account.getAccount();

    if (response.isError || !('account' in response.data)) return {} as Account;

    const res = await sdk.callAction<Account>({
      actionName: 'account/updateSubscription',
      payload: { account: response.data.account, isSubscribed },
    });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const addAddress = useCallback(async (address: Omit<Address, 'addressId'>): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.addAddress(address);

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const addShippingAddress = useCallback(async (address: Omit<Address, 'addressId'>): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const response = await extensions.account.getAccount();

    if (response.isError || !('account' in response.data)) return {} as Account;

    const res = await sdk.callAction<Account>({
      actionName: 'account/addShippingAddress',
      payload: { account: response.data.account, address },
    });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const addBillingAddress = useCallback(async (address: Omit<Address, 'addressId'>): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const response = await extensions.account.getAccount();

    if (response.isError || !('account' in response.data)) return {} as Account;

    const res = await sdk.callAction<Account>({
      actionName: 'account/addBillingAddress',
      payload: { account: response.data.account, address },
    });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const updateAddress = useCallback(async (address: Address): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.updateAddress(address);

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const removeAddress = useCallback(async (addressId: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.removeAddress({ addressId });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const setDefaultBillingAddress = useCallback(async (addressId: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.setDefaultBillingAddress({ addressId });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const setDefaultShippingAddress = useCallback(async (addressId: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.setDefaultShippingAddress({ addressId });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const isOrderApprover = useCallback(async () => {
    const res = await sdk.callAction({ actionName: 'account/isOrderAppover' });

    return (res.isError ? {} : res.data) as boolean;
  }, []);

  const requiresOrderApproval = useCallback(async () => {
    const res = await sdk.callAction({ actionName: 'account/requiresOrderApproval' });

    return (res.isError ? false : res.data) as boolean;
  }, []);

  const refreshAccount = useCallback(async (): Promise<void> => {
    // Update timestamp to trigger components that listen to refresh
    setRefreshTimestamp(Date.now());

    // Use getCustomerGroup which calls getAccountExpandedData internally to get fresh data
    try {
      await sdk.callAction({
        actionName: 'account/getCustomerGroup',
      });

      // Force revalidation of the main account cache with fresh data
      await mutate('/action/account/getAccount', undefined, {
        revalidate: true,
        rollbackOnError: false,
      });
    } catch (error) {
      // Fallback to normal revalidation
      await mutate('/action/account/getAccount', undefined, {
        revalidate: true,
        rollbackOnError: false,
      });
    }
  }, []);

  return {
    ...data,
    shippingAddresses,
    billingAddresses,
    defaultShippingAddress,
    defaultBillingAddress,
    addresses,
    login,
    logout,
    register,
    confirm,
    requestConfirmationEmail,
    changePassword,
    requestPasswordReset,
    resetPassword,
    update,
    addIsSubscribedType,
    updateSubscription,
    addAddress,
    addBillingAddress,
    addShippingAddress,
    updateAddress,
    removeAddress,
    setDefaultBillingAddress,
    setDefaultShippingAddress,
    getCustomerGroup,
    getShippingAddress,
    isOrderApprover,
    requiresOrderApproval,
    refreshAccount,
    refreshTimestamp,
  };
};

export default useAccount;
